import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

interface PageHeaderProps {
  title: string;
  /** Count line under the title, e.g. "12 of 40 jobs". */
  subtitle?: React.ReactNode;
  backTo?: string | number;
  backLabel?: string;
  actions?: React.ReactNode;
  /** Dashboard pages omit the back button */
  showBack?: boolean;
}

/**
 * The page title band described in CLAUDE.md ("Page layout pattern").
 *
 * Full-width bordered band: back button on its own line, then the title with an
 * optional count subtitle on the left and the page actions on the right. The
 * horizontal padding is `px-4 md:px-6` and both halves are load bearing — see the
 * measurement in CLAUDE.md before changing it.
 *
 * Pages that constrain their content with `max-w-[1920px] mx-auto` must render this
 * band inside that same wrapper, so the title and the rows beneath it share one left
 * edge above 1920px too.
 */
export default function PageHeader({
  title,
  subtitle,
  backTo = -1,
  backLabel = "Back",
  actions,
  showBack = true,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (typeof backTo === "number") {
      navigate(backTo);
    } else {
      navigate(backTo);
    }
  };

  return (
    <div className="border-b border-border px-4 md:px-6 py-4">
      {showBack && (
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft size={24} /> {backLabel}
        </button>
      )}
      <div className={`flex items-center justify-between${showBack ? ' mt-2' : ''}`}>
        <div>
          <h1 className="text-base font-bold text-foreground">{title}</h1>
          {subtitle && (
            <p className="text-sm text-fieldiq-muted">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}
